import type { BookingsAnalytics, ProjectAnalytics, SalesAnalytics } from './api'
import { readSnapshot, writeSnapshot } from './overviewSnapshot'

// Module-level stale-while-revalidate cache for project analytics, shared by the
// Resumen (Overview) and Analíticas sections. Switching tabs unmounts a section and
// remounts it, which would otherwise re-fetch and flash a skeleton every time. With
// this cache, a returning section shows the last data instantly and revalidates in
// the background.
//
// Además, la última consulta de cada proyecto se guarda en el navegador
// (`overviewSnapshot`): al abrir el panel otro día las cifras salen al momento
// con lo de la última visita, y se sustituyen al llegar las nuevas.
const cache = new Map<string, ProjectAnalytics>()

// La clave lleva la consulta entera, no sólo el rango: dos tramos distintos —o
// el mismo tramo con otra comparación— son respuestas distintas, y compartir
// entrada haría que al cambiar de comparación se viera la anterior.
function key(projectId: string, queryKey: string): string {
  return `${projectId}:${queryKey}`
}

export function getCachedAnalytics(projectId: string, queryKey: string): ProjectAnalytics | undefined {
  return cache.get(key(projectId, queryKey)) ?? readSnapshot<ProjectAnalytics>(projectId, 'analytics', queryKey)
}

export function setCachedAnalytics(projectId: string, queryKey: string, data: ProjectAnalytics): void {
  cache.set(key(projectId, queryKey), data)
  writeSnapshot(projectId, 'analytics', data, queryKey)
}

/**
 * Lo mismo para las analíticas de venta.
 *
 * Mapa aparte y no una entrada más del de arriba: son respuestas de forma
 * distinta bajo la misma clave `proyecto:consulta`, así que compartir mapa
 * dejaría que una pisara a la otra al mirar el mismo tramo en las dos
 * pantallas.
 */
const salesCache = new Map<string, SalesAnalytics>()

export function getCachedSalesAnalytics(projectId: string, queryKey: string): SalesAnalytics | undefined {
  return salesCache.get(key(projectId, queryKey)) ?? readSnapshot<SalesAnalytics>(projectId, 'sales', queryKey)
}

export function setCachedSalesAnalytics(projectId: string, queryKey: string, data: SalesAnalytics): void {
  salesCache.set(key(projectId, queryKey), data)
  writeSnapshot(projectId, 'sales', data, queryKey)
}

/**
 * Y lo mismo para las de la agenda.
 *
 * Tercer mapa por la misma razón que el segundo: las tres respuestas comparten
 * la clave `proyecto:consulta` y tienen forma distinta, así que un solo mapa
 * dejaría que la última pantalla abierta pisara a las otras dos.
 */
const bookingsCache = new Map<string, BookingsAnalytics>()

export function getCachedBookingsAnalytics(projectId: string, queryKey: string): BookingsAnalytics | undefined {
  return bookingsCache.get(key(projectId, queryKey))
}

export function setCachedBookingsAnalytics(projectId: string, queryKey: string, data: BookingsAnalytics): void {
  bookingsCache.set(key(projectId, queryKey), data)
}
